import { useEffect, useMemo, useState } from 'react'
import {
  useCheckoutParticipantDirectly,
  useGetCheckParticipantsStatus,
} from 'api/Event/Attendance'
import {
  AttendeeStatusParams,
  CheckINOutAttendee,
  CheckinOutAttendeeWithType,
} from 'types/event/eventAttendance'
import Spinner from 'components/Spinner'
import { convertToUtc, forceRefetch, getNameSignature } from 'helper/utility'
import {
  StatusLabelCheckin,
  StatusLabelCheckinPending,
  StatusLabelCheckout,
  StatusLabelCheckoutPending,
  StatusLabelDroppedIn,
  StatusLabelDroppedOut,
  StatusLabelNone,
  StatusLabelPending,
} from './StatusLabel'
import { Avatar } from 'antd'
import { failureMessage, infoMessage } from 'api/global'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import { QueryClient, useQueryClient } from '@tanstack/react-query'
import ButtonSpinner from 'components/global/ButtonSpinner'

interface ParticipantsProps {
  params: AttendeeStatusParams
}

const Participants = ({ params }: ParticipantsProps) => {
  const {
    data: participantStatus,
    isLoading: participantStatusLoading,
    isFetching: participantStatusFetching,
    isError: participantStatusError,
    refetch: refetchParticipantStatus,
  } = useGetCheckParticipantsStatus(params)
  const queryClient = useQueryClient()
  const {
    setCheckInModalOpen,
    setCheckInOutModalOpen,
    setParticipantData,
    setCheckOutModalOpen,
    setDropOutModalOpen,
    setDropInModalOpen,
    setDetailedDropOutModalOpen,
    setDetailedDropInModalOpen,
    setApproveCheckInModal,
  } = eventAttendanceStore(store => ({
    setCheckInModalOpen: store.setCheckInModalOpen,
    setCheckInOutModalOpen: store.setCheckInOutModalOpen,
    setParticipantData: store.setParticipantData,
    setCheckOutModalOpen: store.setCheckOutModalOpen,
    setDropOutModalOpen: store.setDropOutModalOpen,
    setDropInModalOpen: store.setDropInModalOpen,
    setDetailedDropOutModalOpen: store.setDetailedDropOutModalOpen,
    setDetailedDropInModalOpen: store.setDetailedDropInModalOpen,
    setApproveCheckInModal: store.setApproveCheckInModal,
  }))
  const [participants, setParticipants] = useState<any>([])
  const [recentlyCheckedInOut, setRecentlyCheckedInOut] = useState<string[]>([])
  const [loaderId, setLoaderId] = useState<string>('')

  const mapParticipantsWithType = (
    participants: CheckINOutAttendee[],
    type: 'kid' | 'authorizedAdult' | 'guardian' | 'dropInAttendee',
  ) =>
    participants.map((participant: any) => ({
      ...participant,
      type: participant.type ?? type,
    }))

  const {
    mutate: checkoutParticipantDirectMutation,
    isLoading: checkoutParticipantDirectLoading,
  } = useCheckoutParticipantDirectly()

  const getButtonText = (participant: CheckinOutAttendeeWithType) => {
    if (!participant.checkInStatus || participant.checkInStatus === 'pending') {
      return 'Check-in'
    } else if (
      participant.checkInStatus === 'request-check-in' &&
      participant.checkOutStatus === 'pending'
    ) {
      return 'Approve check-in'
    } else if (
      participant.checkInStatus === 'checked-in' &&
      participant.checkOutStatus === 'pending'
    ) {
      return 'Check-out'
    } else if (
      participant.checkInStatus === 'checked-in' &&
      participant.checkOutStatus === 'checked-out'
    ) {
      return 'Check-in'
    } else if (
      participant.checkInStatus === 'request-check-in' &&
      participant.checkOutStatus === 'pending'
    ) {
      return 'Check-in'
    } else if (
      participant.checkInStatus === 'checked-in' &&
      participant.checkOutStatus === 'request-check-out'
    ) {
      return 'Check-out requested'
    } else if (
      participant.checkInStatus === 'droped-in' &&
      participant.checkOutStatus === 'pending'
    ) {
      return 'Drop-out'
    } else if (
      participant.checkInStatus === 'droped-in' &&
      participant.checkOutStatus === 'dropped-out'
    ) {
      return 'Drop-in'
    } else {
      return 'hanlde this'
    }
  }

  const handleButtonClick = (participant: CheckinOutAttendeeWithType) => {
    const today = convertToUtc(new Date())
    const selectedDate = params.date
    if (selectedDate > today) {
      failureMessage('Cannot perform actions for future dates')
      return
    }

    if (getButtonText(participant) === 'Check-in') {
      setParticipantData(participant)
      setCheckInModalOpen(true)
      setCheckInOutModalOpen(false)
    } else if (getButtonText(participant) === 'Check-out') {
      setParticipantData(participant)
      setCheckOutModalOpen(true)
      setCheckInOutModalOpen(false)
    } else if (getButtonText(participant) === 'Approve check-in') {
      setParticipantData(participant)
      setApproveCheckInModal(true)
      setCheckInOutModalOpen(false)
    } else if (getButtonText(participant) === 'Check-out requested') {
      const payload = {
        checkInId: participant.checkInId,
      }
      setLoaderId(participant._id)
      checkoutParticipantDirectMutation(payload, {
        onSuccess: () => {
          setRecentlyCheckedInOut([...recentlyCheckedInOut, participant._id])
          setLoaderId('')
        },
        onError: () => {
          setLoaderId('')
        },
      })
    } else if (
      getButtonText(participant) === 'Drop-out' &&
      !participant.familyId
    ) {
      forceRefetch('get-family-members-by-email', queryClient)
      setParticipantData(participant)
      setDropOutModalOpen(true)
      setCheckInOutModalOpen(false)
    }
    //  else if (
    //   getButtonText(participant) === 'Drop-in' &&
    //   !participant.familyId
    // ) {
    //   forceRefetch('get-family-members-by-email', queryClient)
    //   setParticipantData(participant)
    //   setDropInModalOpen(true)
    //   setCheckInOutModalOpen(false)
    // }
    else if (
      getButtonText(participant) === 'Drop-out' &&
      participant.familyId
    ) {
      setParticipantData(participant)
      setDetailedDropOutModalOpen(true)
      setCheckInOutModalOpen(false)
    } else if (getButtonText(participant) === 'Drop-in') {
      setParticipantData(participant)
      setDetailedDropInModalOpen(true)
      setCheckInOutModalOpen(false)
    } else {
      infoMessage('something wrong went in backend')
    }
  }

  useEffect(() => {
    refetchParticipantStatus()
  }, [params])

  // Clear recently checked in/out participants when date changes
  useEffect(() => {
    setRecentlyCheckedInOut([])
  }, [params.date])

  useMemo(() => {
    if (participantStatus) {
      const allParticipants = [
        ...mapParticipantsWithType(participantStatus.kids, 'kid'),
        ...mapParticipantsWithType(
          participantStatus.authorizedAdults,
          'authorizedAdult',
        ),
        ...mapParticipantsWithType(participantStatus.guardians, 'guardian'),
        ...mapParticipantsWithType(
          participantStatus.dropinAttendees || [],
          'dropInAttendee',
        ),
      ]
      setParticipants(allParticipants)
    }
  }, [participantStatus])
  if (participantStatusLoading || participantStatusFetching) {
    return (
      <div className="flex py-40px justify-center">
        <Spinner />
      </div>
    )
  }

  if (participantStatusError) {
    return <div>Something went wrong</div>
  }

  return (
    <>
      {participants.length ? (
        <>
          {participants.map(
            (participant: CheckinOutAttendeeWithType, index: number) => {
              return (
                <div
                  className={`py-15px grid grid-33 participants ${
                    index === participants.length - 1 ? '' : 'border-btm'
                  }`}
                  key={participant._id}
                >
                  <div className="flex-center">
                    <div className="w-40px">
                      <Avatar size={40} src={participant.photo?.post}>
                        {getNameSignature(
                          `${participant.firstName} ${participant.lastName}`,
                        )}
                      </Avatar>
                    </div>
                    <h3 className="text-14 w-500 ml-10">
                      {participant.firstName} {participant.lastName}
                    </h3>
                  </div>

                  <div className="ml-40px">
                    {!participant.checkInStatus ||
                    participant.checkInStatus === 'pending' ? (
                      <StatusLabelNone
                        participant={participant}
                        recentlyCheckedInOut={recentlyCheckedInOut}
                        setRecentlyCheckedInOut={setRecentlyCheckedInOut}
                      />
                    ) : participant.checkInStatus === 'checked-in' &&
                      participant.checkOutStatus === 'pending' ? (
                      <StatusLabelCheckin
                        participant={participant}
                        recentlyCheckedInOut={recentlyCheckedInOut}
                        setRecentlyCheckedInOut={setRecentlyCheckedInOut}
                      />
                    ) : participant.checkInStatus === 'checked-in' &&
                      participant.checkOutStatus === 'checked-out' ? (
                      <StatusLabelCheckout
                        participant={participant}
                        recentlyCheckedInOut={recentlyCheckedInOut}
                        setRecentlyCheckedInOut={setRecentlyCheckedInOut}
                      />
                    ) : participant.checkInStatus === 'request-check-in' &&
                      participant.checkOutStatus === 'pending' ? (
                      <StatusLabelCheckinPending
                        participant={participant}
                        recentlyCheckedInOut={recentlyCheckedInOut}
                        setRecentlyCheckedInOut={setRecentlyCheckedInOut}
                      />
                    ) : participant.checkInStatus === 'checked-in' &&
                      participant.checkOutStatus === 'request-check-out' ? (
                      <StatusLabelCheckoutPending
                        participant={participant}
                        recentlyCheckedInOut={recentlyCheckedInOut}
                        setRecentlyCheckedInOut={setRecentlyCheckedInOut}
                      />
                    ) : participant.checkInStatus === 'droped-in' &&
                      participant.checkOutStatus === 'pending' ? (
                      <StatusLabelDroppedIn />
                    ) : participant.checkInStatus === 'droped-in' &&
                      participant.checkOutStatus === 'dropped-out' ? (
                      <StatusLabelDroppedOut />
                    ) : (
                      <div>This needs to be handled</div>
                    )}
                  </div>

                  <div className="ml-20px flex justify-end">
                    <button
                      className={`btn-borderless w-500 ${
                        getButtonText(participant) === 'Check-in' ||
                        getButtonText(participant) === 'Approve check-in'
                          ? 'text-prim'
                          : 'danger-txt'
                      }`}
                      onClick={() => handleButtonClick(participant)}
                    >
                      {loaderId === participant._id ? (
                        <ButtonSpinner size={20} color="#000" />
                      ) : (
                        getButtonText(participant)
                      )}
                    </button>
                  </div>
                </div>
              )
            },
          )}
        </>
      ) : (
        <p>No participants found for this date</p>
      )}
    </>
  )
}

export default Participants
